/**
 * The post-sign-in destination carried in `?next=`, or `fallback` when it is
 * missing or unsafe.
 *
 * Only a same-origin, root-relative path is honoured — never an absolute URL,
 * a protocol-relative `//host`, or a `/\host` that browsers normalise to one —
 * so `?next=` can never become an open redirect. Auth screens (`/login` …) are
 * refused so a stale `next` can't bounce the user back to the form they just
 * submitted.
 */
const AUTH_PATHS = /^\/(login|signup|forgot-password|reset-password|logout)(?:[/?#]|$)/i;
const SAME_ORIGIN = "https://same-origin.invalid";

export function safeNextPath(search: string | null | undefined, fallback = "/dashboard"): string {
  if (!search) return fallback;
  const raw = new URLSearchParams(search).get("next");
  if (!raw) return fallback;
  // Whitespace, control characters and backslashes are how `/\evil.com` or a
  // tab-prefixed `//evil.com` slip past a naive "starts with one slash" check.
  // eslint-disable-next-line no-control-regex
  if (/[ -\s\\]/.test(raw)) return fallback;
  if (!raw.startsWith("/") || raw.startsWith("//")) return fallback;
  // Belt and braces: resolve against a dummy origin and require it to stay there.
  try {
    const url = new URL(raw, SAME_ORIGIN);
    if (url.origin !== SAME_ORIGIN) return fallback;
    const path = `${url.pathname}${url.search}${url.hash}`;
    return AUTH_PATHS.test(path) ? fallback : path;
  } catch {
    return fallback;
  }
}
